<template>
  <div v-if="hasWillVersion">
    <h3>Upload copy of physical will received by admin</h3>
    <p>Only PDF documents allowed</p>

    <v-file-input
      v-model="file"
      single-line
      outlined
      dense
      filled
      :loading="buttonLoading"
      accept="application/pdf"
      label="Upload will"
      class="file-upload-btn"
    />

    <WillUploadProgress
      :loading="buttonLoading"
      :message="'Uploading the will, this can take a few seconds.'"
    />

    <ErrorSnackbar v-model="showError" :error="errorMessage" />
    <SuccessSnackbar v-model="showSuccess" :message="successMessage" />

    <v-dialog v-model="identityMismatchDialog" max-width="560" persistent>
      <WillIdentityMismatchDialog
        :message="identityMismatchMessage"
        :prompt="identityMismatchPrompt"
        :loading="identityMismatchConfirming"
        @confirm="confirmIdentityMismatch"
        @cancel="cancelIdentityMismatch"
      />
    </v-dialog>
  </div>
  <div v-else>
    <h3>{{ testator.fullName }} does not have any existing will versions.</h3>
    <p>Please complete their will and download it before trying to upload.</p>
  </div>
</template>

<script>
  import SuccessSnackbar from '../../../common/SuccessSnackbar.vue';
  import ErrorSnackbar from '../../../common/ErrorSnackbar.vue';
  import { formHelperMixin } from '../../../../mixins/form-helper-mixin';
  import { willIdentityMismatchMixin } from '../../../../mixins/will-identity-mismatch-mixin';
  import WillIdentityMismatchDialog from '../../../common/WillIdentityMismatchDialog.vue';
  import WillUploadProgress from '../../../common/WillUploadProgress.vue';
  import { TESTATOR_QUERY } from '../../../../constants/graphql/testators';
  import { UPLOAD_WILL_MUTATION } from '../../../../constants/graphql/will-verifications';

  export default {
    name: 'WillVerificationBlock',
    components: {
      ErrorSnackbar,
      SuccessSnackbar,
      WillIdentityMismatchDialog,
      WillUploadProgress,
    },
    mixins: [formHelperMixin, willIdentityMismatchMixin],
    props: {
      testator: {
        required: true,
        type: Object,
      },
    },
    data() {
      return {
        file: null,
        showSuccess: false,
        successMessage: null,
        errorMessage: null,
      };
    },
    watch: {
      file(newValue) {
        if (newValue) {
          this.uploadWill();
        }
      },
    },
    computed: {
      hasWillVersion() {
        return !!this.testator.willVersions.length;
      },
    },
    methods: {
      uploadWill() {
        this.buttonLoading = 'success';
        var reader = new FileReader();
        reader.readAsDataURL(this.file);
        reader.onload = function (evt) {
          const fileBase64 = evt.target.result;
          this.performMutation(fileBase64);
        }.bind(this);
      },
      performMutation(fileBase64) {
        this.performUpload(fileBase64, false);
      },
      performUpload(fileBase64, acknowledgedMismatch) {
        const testatorId = this.testator.id;

        this.$apollo
          .mutate({
            mutation: UPLOAD_WILL_MUTATION,
            variables: {
              testatorId,
              fileBase64,
              acknowledgedMismatch,
            },
            update: (cache, { data: { uploadWill } }) => {
              this.updateCacheAfterUploadWill(cache, uploadWill);
            },
          })
          .then(_response => {
            this.showSuccessMessage();
          })
          .catch(error => {
            if (this.handleIdentityMismatch(error, fileBase64)) return;

            if (
              error.message.includes(
                'GraphQL error: Will version is already valid'
              )
            ) {
              this.errorMessage =
                'The latest will version already has a valid signed and uploaded will';
            } else if (
              error.message.includes('GraphQL error: Invalid file type')
            ) {
              this.errorMessage = 'Invalid file type. Please upload a pdf.';
            } else {
              this.reportUnexpectedUploadError(error);
              return;
            }
            this.displayError();
            this.file = null;
          });
      },
      updateCacheAfterUploadWill(cache, uploadWill) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testator.id },
        };
        const data = cache.readQuery(query);

        var willVersion = data.testator.willVersions.find(
          willVersion => willVersion.id == uploadWill.willVersionId
        );
        willVersion.uploadedWills.push(uploadWill);

        cache.writeQuery({ ...query, data });
      },
      showSuccessMessage() {
        this.buttonLoading = false;
        this.file = null;
        this.successMessage = 'Will successfully uploaded';
        this.showSuccess = true;
      },
    },
  };
</script>
